<template>
  <div class="ca-usuarios">
    <header class="ca-cabecera">
      <div>
        <h1 class="ca-titulo">Usuarios y permisos personalizados</h1>
        <p class="ca-subtitulo">Asigna roles, define permisos personalizados y consulta los limites de tu plan.</p>
      </div>
    </header>

    <!-- Banner de limites del plan -->
    <div v-if="limites" class="ca-banner" :class="{ 'ca-banner--alerta': sinCupoOPlan }">
      <div>
        <strong>{{ limites.plan_nombre || 'Sin plan activo' }}</strong>
        <span v-if="limites.permite_gestion_usuarios"> &middot; usuarios usados {{ limites.usados }} de {{ limites.max_usuarios !== null ? limites.max_usuarios : 'ilimitado' }}</span>
        <span v-else> &middot; este plan no permite gestion de usuarios.</span>
      </div>
      <div v-if="sinCupoOPlan" class="ca-banner__msg">
        <i class="bi bi-exclamation-triangle"></i> No puedes crear mas usuarios.
      </div>
    </div>

    <div v-if="cargando" class="ca-info">Cargando...</div>

    <!-- Tabla de usuarios -->
    <table v-else class="ca-tabla">
      <thead>
        <tr>
          <th>Usuario</th>
          <th>Correo</th>
          <th>Roles asignados</th>
          <th>Estado</th>
          <th>Tipo</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="u in usuarios" :key="u.id">
          <td>{{ u.nombre_completo || u.nombres_apellidos || u.nombre_usuario || '-' }}</td>
          <td>{{ u.correo_cliente }}</td>
          <td>
            <span v-if="!u.rol_nombres.length" class="ca-text-muted">sin rol</span>
            <span v-for="(n, i) in u.rol_nombres" :key="i" class="ca-tag">{{ n }}</span>
          </td>
          <td><span :class="['ca-badge', u.estado ? 'ca-badge--exito' : 'ca-badge--neutro']">{{ u.estado ? 'activo' : 'inactivo' }}</span></td>
          <td>
            <span v-if="u.es_administrador" class="ca-badge ca-badge--info">administrador</span>
            <span v-else class="ca-text-muted">usuario</span>
          </td>
          <td class="ca-tabla__acciones">
            <button class="ca-boton ca-boton--xs ca-boton--secundario" @click="abrirAsignarRol(u)" :disabled="u.es_administrador">
              <i class="bi bi-shield-check"></i> Roles
            </button>
            <button class="ca-boton ca-boton--xs ca-boton--secundario" @click="abrirPersonalizar(u)" :disabled="u.es_administrador">
              <i class="bi bi-sliders"></i> Personalizar
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal asignar roles -->
    <div v-if="modalRoles" class="ca-modal-fondo" @click.self="modalRoles = null">
      <div class="ca-modal">
        <h2>Roles para "{{ modalRoles.usuario.correo_cliente }}"</h2>
        <p class="ca-subtitulo">Selecciona los roles que tendra este usuario.</p>
        <div v-if="!roles.length" class="ca-info">No hay roles definidos. Crea uno primero en Roles y permisos.</div>
        <div v-else class="ca-roles-lista">
          <label v-for="r in roles" :key="r.id" class="ca-check">
            <input type="checkbox" :value="r.id" v-model="modalRoles.seleccion"/>
            <strong>{{ r.nombre }}</strong>
            <small class="ca-text-muted">{{ r.codigo }}</small>
          </label>
        </div>
        <div class="ca-modal__acciones">
          <button class="ca-boton ca-boton--secundario" @click="modalRoles = null">Cancelar</button>
          <button class="ca-boton ca-boton--primario" @click="guardarRoles" :disabled="guardando">Guardar</button>
        </div>
      </div>
    </div>

    <!-- Modal personalizar permisos -->
    <div v-if="modalPers" class="ca-modal-fondo" @click.self="modalPers = null">
      <div class="ca-modal ca-modal--ancho">
        <h2>Permisos personalizados de "{{ modalPers.usuario.correo_cliente }}"</h2>
        <p class="ca-subtitulo">Otorga o niega acciones puntuales que prevalecen sobre los permisos del rol.</p>

        <div v-if="!modulosCatalogo.length" class="ca-info">Cargando catalogo...</div>
        <div v-else class="ca-matriz">
          <table>
            <thead>
              <tr>
                <th>Submodulo</th>
                <th v-for="a in acciones" :key="a">{{ a }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="m in modulosCatalogo" :key="m.id">
                <tr class="ca-fila-modulo"><td colspan="5"><strong>{{ m.nombre }}</strong></td></tr>
                <tr v-for="s in m.submodulos" :key="s.id">
                  <td class="ca-celda-submodulo">{{ s.nombre }} <code class="ca-ruta">{{ s.ruta }}</code></td>
                  <td v-for="a in acciones" :key="a" class="ca-celda-accion">
                    <select :value="estadoOverride(s.id, a)" @change="aplicarOverride(s.id, a, $event.target.value)">
                      <option value="">heredar</option>
                      <option value="1">otorgar</option>
                      <option value="0">negar</option>
                    </select>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="ca-modal__acciones">
          <button class="ca-boton ca-boton--primario" @click="modalPers = null">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import controlAcceso from '@/services/controlAccesoService'
import { notifySuccess, notifyError } from '@/services/notificationService'

const ACCIONES = ['ver', 'crear', 'editar', 'eliminar']

export default {
  name: 'ClientControlAccesoUsuarios',
  data() {
    return {
      cargando: false,
      guardando: false,
      acciones: ACCIONES,
      usuarios: [],
      roles: [],
      modulosCatalogo: [],
      limites: null,
      modalRoles: null,
      modalPers: null,
      // Map: 'sid.accion' -> '1' | '0'
      overrides: {},
    }
  },
  computed: {
    sinCupoOPlan() {
      if (!this.limites) return false
      if (!this.limites.permite_gestion_usuarios) return true
      if (this.limites.max_usuarios === null) return false
      return this.limites.usados >= this.limites.max_usuarios
    },
  },
  mounted() { this.cargar() },
  methods: {
    async cargar() {
      this.cargando = true
      try {
        const [resU, resR, resL, resM] = await Promise.all([
          controlAcceso.listarUsuarios(),
          controlAcceso.listarRoles(),
          controlAcceso.limitesUsuarios(),
          controlAcceso.listarModulos(),
        ])
        this.usuarios = resU.data || []
        this.roles = resR.data || []
        this.limites = resL.data || null
        this.modulosCatalogo = resM.data || []
      } catch (e) {
        notifyError('No se pudo cargar la informacion.')
      } finally { this.cargando = false }
    },
    abrirAsignarRol(u) {
      this.modalRoles = { usuario: u, seleccion: [...(u.rol_ids || [])] }
    },
    async guardarRoles() {
      this.guardando = true
      try {
        await controlAcceso.setRolesUsuario(this.modalRoles.usuario.id, this.modalRoles.seleccion)
        notifySuccess('Roles actualizados.')
        this.modalRoles = null
        await this.cargar()
      } catch (e) {
        notifyError(e?.response?.data?.error || 'Error al guardar.')
      } finally { this.guardando = false }
    },
    async abrirPersonalizar(u) {
      // Los overrides actuales del usuario no estan expuestos por id de submodulo
      // en el endpoint de permisos efectivos. La matriz arranca en "heredar" y
      // los cambios se persisten en cada interaccion.
      this.overrides = {}
      this.modalPers = { usuario: u }
    },
    estadoOverride(sid, accion) {
      const v = this.overrides[`${sid}.${accion}`]
      return v === undefined ? '' : v
    },
    async aplicarOverride(sid, accion, valor) {
      try {
        if (valor === '') {
          await controlAcceso.quitarPermisoPersonalizado(this.modalPers.usuario.id, sid, accion)
          delete this.overrides[`${sid}.${accion}`]
        } else {
          await controlAcceso.setPermisoPersonalizado(this.modalPers.usuario.id, sid, accion, valor === '1')
          this.overrides[`${sid}.${accion}`] = valor
        }
        this.overrides = { ...this.overrides }
        notifySuccess('Permiso personalizado actualizado.')
      } catch (e) {
        notifyError(e?.response?.data?.error || 'Error al guardar.')
      }
    },
  },
}
</script>

<style scoped>
.ca-usuarios { color: var(--text-primary); }
.ca-cabecera { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; gap: 16px; }
.ca-titulo { font-size: 1.4rem; margin: 0; }
.ca-subtitulo { margin: 4px 0 0; color: var(--text-secondary); font-size: 0.88rem; }

.ca-banner {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
}
.ca-banner--alerta {
  border-color: color-mix(in srgb, var(--accent-warning) 50%, transparent);
  background: color-mix(in srgb, var(--accent-warning) 10%, transparent);
}
.ca-banner__msg { color: var(--accent-warning); font-weight: 600; }

.ca-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  overflow: hidden;
}
.ca-tabla th, .ca-tabla td { padding: 10px 12px; border-bottom: 1px solid var(--border-main); text-align: left; }
.ca-tabla th { font-weight: 600; color: var(--text-secondary); background: var(--bg-main); }
.ca-tabla tbody tr:last-child td { border-bottom: 0; }
.ca-tabla__acciones { display: flex; gap: 6px; }

.ca-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--bg-main);
  color: var(--text-primary);
  font-size: 0.78rem;
  margin-right: 4px;
}
.ca-text-muted { color: var(--text-muted); font-size: 0.85rem; }
.ca-codigo {
  display: inline-block; padding: 1px 6px; border-radius: 6px;
  background: var(--bg-main); color: var(--text-secondary);
  font-size: 0.74rem; font-family: ui-monospace, monospace;
}
.ca-ruta { color: var(--text-muted); font-size: 0.78rem; margin-left: 6px; }

.ca-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 600; }
.ca-badge--exito  { background: color-mix(in srgb, var(--accent-success) 18%, transparent); color: var(--accent-success); }
.ca-badge--info   { background: color-mix(in srgb, var(--primary-main) 18%, transparent);   color: var(--primary-main); }
.ca-badge--neutro { background: var(--bg-main); color: var(--text-muted); }

.ca-info { padding: 14px; color: var(--text-secondary); }

.ca-roles-lista { display: flex; flex-direction: column; gap: 8px; max-height: 280px; overflow-y: auto; }

.ca-boton {
  border: 1px solid var(--border-main);
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ca-boton:hover:not(:disabled) { background: var(--bg-main); }
.ca-boton:disabled { opacity: 0.5; cursor: not-allowed; }
.ca-boton--xs { padding: 4px 8px; font-size: 0.78rem; }
.ca-boton--primario { background: var(--primary-main); border-color: var(--primary-main); color: #fff; }
.ca-boton--primario:hover:not(:disabled) { background: var(--primary-hover); border-color: var(--primary-hover); }
.ca-boton--secundario { background: transparent; }

.ca-modal-fondo { position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; }
.ca-modal {
  background: var(--secondary-bg-main); color: var(--text-primary);
  border-radius: 12px; padding: 22px; min-width: 420px; max-width: 560px;
  border: 1px solid var(--border-main); box-shadow: var(--shadow-md);
}
.ca-modal--ancho { min-width: 720px; max-width: 860px; max-height: 80vh; overflow-y: auto; }
.ca-modal h2 { font-size: 1.1rem; margin: 0 0 12px; }
.ca-modal__acciones { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

.ca-matriz { overflow-x: auto; border: 1px solid var(--border-main); border-radius: 8px; margin-top: 8px; }
.ca-matriz table { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.ca-matriz th, .ca-matriz td { padding: 6px 10px; border-bottom: 1px solid var(--border-main); text-align: left; }
.ca-matriz th { background: var(--bg-main); font-weight: 600; color: var(--text-secondary); }
.ca-fila-modulo td { background: var(--bg-main); color: var(--text-secondary); padding: 6px 12px; }
.ca-celda-submodulo { padding-left: 20px; }
.ca-celda-accion { text-align: center; }
.ca-celda-accion select {
  background: var(--bg-main); color: var(--text-primary); border: 1px solid var(--border-main);
  border-radius: 6px; padding: 2px 6px; font-size: 0.78rem;
}

.ca-check { display: flex; align-items: center; gap: 8px; padding: 6px 0; }
</style>
